import { Calendar, ChevronDown, Edit, Info, Landmark, X } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import type { AllTransactionViewNode } from "@/api/models/allTransactionView";
import type { TransactionCategoryApi } from "@/api/models/transactionCategory";
import {
	type AllTransactionViewSortInput,
	getAllTransactionsGraphql,
} from "@/api/services/allTransactionGraphqlService";
import { updateBankTransactionCategory } from "@/api/services/bankTransactionService";
import { updatePurchaseTransactionCategory } from "@/api/services/purchaseTransactionService";
import { updateSalesTransactionCategory } from "@/api/services/salesTransactionService";
import { getAllTransactionCategories } from "@/api/services/transactionCategoryService";
import { FeatureFeedback } from "@/components/FeatureFeedback";
import {
	type ColumnConfig,
	MaximusTableReusable,
} from "@/components/MaximusTableReusable";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";
import { useApp } from "../../contexts/AppContext";
import { DEMO_TRANSACTIONS } from "../../data/demo-data";
import { useEntityFilter } from "../../hooks/useEntityFilter";
import { useFormatting } from "../../hooks/useFormatting";
import { formatDate } from "../../lib/utils";
import { TransactionDetailModal } from "../modals/TransactionDetailModal";
import { PermissionGate } from "../permissions/PermissionGate";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

type FilterType = "all" | "overdue" | "thisWeek" | "uncategorized";

interface TransactionDisplay {
	id: string | number;
	date: Date;
	dueDate?: Date;
	description: string;
	category: string;
	status: string;
	grossValueIn: number;
	grossValueOut: number;
	amount: number;
	source?: string;
	reference?: string;
	accountName?: string;
	type?: "income" | "expense";
	transactionType?: string;
	outstanding?: number;
	dataSourceName?: string;
}

interface TransactionsPageProps {
	accountFilter?: string;
}

const SORT_FIELD_MAP: Record<string, string> = {
	source: "Source",
	dataSourceName: "DataSourceName",
	reference: "Reference",
	accountName: "AccountName",
	description: "Description",
	transactionType: "TransactionType",
	category: "Category",
	date: "TransactionDate",
	dueDate: "DueDate",
	amount: "Amount",
	outstanding: "Outstanding",
	status: "Status",
};

export const TransactionsPage: React.FC<TransactionsPageProps> = ({
	accountFilter,
}) => {
	const { auditMode, mode } = useApp();
	const { hasPermission } = usePermissions();
	const { filterByEntity } = useEntityFilter();
	const { formatCurrency } = useFormatting();
	const { toast } = useToast();
	const [filter, setFilter] = useState<FilterType>("all");
	const [selectedTransaction, setSelectedTransaction] =
		useState<TransactionDisplay | null>(null);
	const [showTransactionModal, setShowTransactionModal] = useState(false);
	const [liveTransactions, setLiveTransactions] = useState<
		TransactionDisplay[]
	>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [validCategories, setValidCategories] = useState<
		TransactionCategoryApi[]
	>([]);
	const [activeAccountFilter, setActiveAccountFilter] = useState<
		string | undefined
	>(accountFilter);
	const [bannerExpanded, setBannerExpanded] = useState(false);
	const [sortParams, setSortParams] = useState<
		AllTransactionViewSortInput[] | undefined
	>(undefined);

	// ── API calls ────────────────────────────────────────────────────────────
	const fetchTransactions = async (sort?: AllTransactionViewSortInput[]) => {
		setIsLoading(true);
		setError(null);
		try {
			const result = await getAllTransactionsGraphql(
				undefined,
				500,
				undefined,
				sort,
			);
			const mapped = result.edges.map((edge) => {
				const t: AllTransactionViewNode = edge.node;
				const amt = t.amount ?? 0;
				return {
					id: `${t.source}-${t.id}-${edge.cursor}`,
					date: t.transactionDate ? new Date(t.transactionDate) : new Date(),
					dueDate: t.dueDate ? new Date(t.dueDate) : undefined,
					description: t.description || "No description",
					category: t.category || "Uncategorised",
					status: t.status || "Unknown",
					amount: amt,
					grossValueIn: amt > 0 ? amt : 0,
					grossValueOut: amt < 0 ? Math.abs(amt) : 0,
					source: t.source,
					reference: t.reference ?? undefined,
					accountName: t.accountName ?? undefined,
					type: (amt >= 0 ? "income" : "expense") as "income" | "expense",
					transactionType: t.transactionType ?? undefined,
					outstanding: t.outstanding ?? undefined,
					dataSourceName: t.dataSourceName ?? undefined,
				};
			});
			setLiveTransactions(mapped);
		} catch (err) {
			console.error("Failed to fetch transactions:", err);
			setError("Failed to load transactions");
		} finally {
			setIsLoading(false);
		}
	};

	const fetchCategories = async () => {
		try {
			const response = await getAllTransactionCategories();
			setValidCategories(response.categories);
		} catch (err) {
			console.error("Failed to fetch categories:", err);
		}
	};

	useEffect(() => {
		if (mode === "live") {
			fetchTransactions(sortParams);
			fetchCategories();
		}
	}, [mode, sortParams]);

	const handleSortChange = useCallback(
		(column: string | null, direction: "asc" | "desc" | null) => {
			if (!column || !direction) {
				setSortParams(undefined);
				return;
			}
			const field = SORT_FIELD_MAP[column];
			if (!field) {
				setSortParams(undefined);
				return;
			}
			setSortParams([
				{
					field,
					order: direction === "asc" ? "Ascending" : "Descending",
				},
			]);
		},
		[],
	);

	const handleCategorySave = async (
		transaction: TransactionDisplay,
		newCategory: string,
	) => {
		if (mode !== "live" || !transaction.source) {
			console.log("Demo mode - category change not persisted:", {
				transaction,
				newCategory,
			});
			return;
		}
		if (!newCategory || newCategory.trim() === "") {
			setError("Category cannot be empty");
			return;
		}
		const isValidCategory = validCategories.some(
			(cat) => cat.name === newCategory,
		);
		if (!isValidCategory) {
			setError(
				`Invalid category: "${newCategory}". Please select a valid category.`,
			);
			return;
		}
		try {
			const numericId = Number(transaction.id);
			switch (transaction.source) {
				case "Sales Ledger":
					await updateSalesTransactionCategory(numericId, newCategory);
					break;
				case "Purchase Ledger":
					await updatePurchaseTransactionCategory(numericId, newCategory);
					break;
				case "Bank":
					await updateBankTransactionCategory(numericId, newCategory);
					break;
				default:
					console.error("Unknown transaction source:", transaction.source);
					return;
			}
			await fetchTransactions();
			setError(null);
			toast({
				title: "Category Updated",
				description: `Category changed to "${newCategory}".`,
			});
		} catch (err) {
			console.error("Failed to update category:", err);
			setError("Failed to update category");
		}
	};

	// ── Data assembly ────────────────────────────────────────────────────────
	const demoTransactions = [...DEMO_TRANSACTIONS];

	const allTransactions: TransactionDisplay[] =
		mode === "live"
			? liveTransactions
			: filterByEntity(demoTransactions).map((t) => ({
					id: t.id,
					date: t.date,
					dueDate: t.dueDate,
					description: t.description,
					category: t.category,
					status: t.status,
					amount: t.amount,
					grossValueIn: t.amount > 0 ? t.amount : 0,
					grossValueOut: t.amount < 0 ? Math.abs(t.amount) : 0,
					type: (t.amount >= 0 ? "income" : "expense") as "income" | "expense",
					source: t.source,
					reference: t.reference,
					accountName: t.accountName,
					transactionType: t.transactionType,
					outstanding: t.outstanding,
					dataSourceName: t.dataSourceName,
				}));

	const entityFilteredTransactions = allTransactions;

	// ── Quick filter counts ──────────────────────────────────────────────────
	const getFilterCount = (filterType: FilterType) => {
		if (filterType === "all") return entityFilteredTransactions.length;
		if (filterType === "overdue") {
			return entityFilteredTransactions.filter((t) => t.status === "Overdue")
				.length;
		}
		if (filterType === "uncategorized") {
			return entityFilteredTransactions.filter(
				(t) => t.category === "Uncategorised",
			).length;
		}
		if (filterType === "thisWeek") {
			const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
			return entityFilteredTransactions.filter((t) => t.date >= weekAgo).length;
		}
		return 0;
	};

	// ── Pre-filter for quick filter chips ───────────────────────────────────
	const quickFilteredTransactions = entityFilteredTransactions.filter((txn) => {
		if (filter === "overdue") return txn.status === "Overdue";
		if (filter === "uncategorized") return txn.category === "Uncategorised";
		if (filter === "thisWeek") {
			const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
			return txn.date >= weekAgo;
		}
		return true;
	});

	// ── Column configuration ─────────────────────────────────────────────────
	const columns: ColumnConfig<TransactionDisplay>[] = [
		{
			key: "source",
			label: "Source",
			sortable: true,
			filterable: true,
			filterConfig: {
				type: "select",
				options: [
					"Sales Ledger",
					"Purchase Ledger",
					"Bank",
					"Sales Order",
					"Purchase Order",
				],
			},
			align: "left",
			width: 140,
			render: (row) => <span className="text-sm">{row.source ?? "—"}</span>,
		},
		{
			key: "dataSourceName",
			label: "Data Source",
			sortable: true,
			filterable: true,
			filterConfig: { type: "text" },
			align: "left",
			width: 150,
			render: (row) => (
				<span className="text-sm text-muted-foreground">
					{row.dataSourceName ?? "—"}
				</span>
			),
		},
		{
			key: "reference",
			label: "Reference",
			sortable: true,
			filterable: true,
			filterConfig: { type: "text" },
			align: "left",
			width: 130,
			render: (row) => (
				<span className="text-sm font-mono">{row.reference ?? "—"}</span>
			),
		},
		{
			key: "accountName",
			label: "Account Name",
			sortable: true,
			filterable: true,
			filterConfig: { type: "text" },
			align: "left",
			width: 160,
			render: (row) => (
				<span className="text-sm">{row.accountName ?? "—"}</span>
			),
		},
		{
			key: "description",
			label: "Description",
			sortable: true,
			filterable: true,
			filterConfig: { type: "text" },
			align: "left",
			width: 240,
			render: (row) => (
				<div className="max-w-xs truncate text-sm">{row.description}</div>
			),
		},
		{
			key: "transactionType",
			label: "Transaction Type",
			sortable: true,
			filterable: true,
			filterConfig: { type: "text" },
			align: "left",
			width: 140,
			render: (row) => (
				<span className="text-sm">{row.transactionType ?? "—"}</span>
			),
		},
		{
			key: "category",
			label: "Category",
			sortable: true,
			filterable: true,
			filterConfig: {
				type: "select",
				options: [
					"Customer Receipts",
					"Other Income",
					"Materials",
					"Payroll",
					"Subcontractors",
					"Rent",
					"Equipment",
					"Uncategorised",
				],
			},
			align: "left",
			width: 150,
			render: (row) => (
				<span
					className={
						row.category === "Uncategorised"
							? "text-destructive text-sm"
							: "text-sm"
					}
				>
					{row.category}
				</span>
			),
		},
		{
			key: "date",
			label: "Transaction Date",
			sortable: true,
			filterable: true,
			filterConfig: { type: "date-range" },
			align: "left",
			width: 140,
			render: (row) => (
				<div className="flex items-center gap-1 text-sm">
					<Calendar className="h-3 w-3 text-muted-foreground" />
					{formatDate(row.date)}
				</div>
			),
		},
		{
			key: "dueDate",
			label: "Due Date",
			sortable: true,
			filterable: true,
			filterConfig: { type: "date-range" },
			align: "left",
			width: 130,
			render: (row) =>
				row.dueDate ? (
					<div
						className={cn(
							"flex items-center gap-1 text-sm",
							row.status === "Overdue" && "text-destructive font-semibold",
						)}
					>
						<Calendar className="h-3 w-3" />
						{formatDate(row.dueDate)}
					</div>
				) : (
					<span className="text-muted-foreground">—</span>
				),
		},
		{
			key: "amount",
			label: "Amount",
			sortable: true,
			filterable: true,
			filterConfig: { type: "number" },
			align: "right",
			width: 130,
			render: (row) => (
				<span
					className={cn(
						"font-semibold",
						row.grossValueIn > 0 ? "text-success-600" : "text-destructive",
					)}
				>
					{row.grossValueIn > 0 ? "+" : "-"}
					{formatCurrency(
						row.grossValueIn > 0 ? row.grossValueIn : row.grossValueOut,
						"full",
					)}
				</span>
			),
		},
		{
			key: "outstanding",
			label: "Outstanding",
			sortable: true,
			filterable: true,
			filterConfig: { type: "number" },
			align: "right",
			width: 120,
			render: (row) =>
				row.outstanding != null ? (
					<span className="text-sm font-semibold">
						{formatCurrency(row.outstanding, "full")}
					</span>
				) : (
					<span className="text-muted-foreground">—</span>
				),
		},
		{
			key: "status",
			label: "Status",
			sortable: true,
			filterable: true,
			filterConfig: {
				type: "select",
				options: ["Outstanding", "Overdue", "Settled", "Unknown"],
			},
			align: "left",
			width: 120,
			render: (row) => {
				const variant =
					row.status === "Settled"
						? "success"
						: row.status === "Outstanding"
							? "warning"
							: row.status === "Overdue"
								? "destructive"
								: row.status === "Unknown"
									? "secondary"
									: "info";
				return <Badge variant={variant}>{row.status}</Badge>;
			},
		},
		{
			key: "id",
			label: "Actions",
			sortable: false,
			filterable: false,
			align: "center",
			width: 80,
			render: (row) => (
				<Button
					size="sm"
					variant="ghost"
					onClick={(e) => {
						e.stopPropagation();
						setSelectedTransaction(row);
						setShowTransactionModal(true);
					}}
				>
					<Edit className="h-4 w-4" />
				</Button>
			),
		},
	];

	// ── Render ───────────────────────────────────────────────────────────────
	return (
		<div className="flex flex-col h-full space-y-2 px-2 pt-0 pb-2 overflow-y-auto">
			{/* Scope guidance banner */}
			<div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
				<button
					type="button"
					className="flex w-full items-start gap-3 text-left"
					onClick={() => setBannerExpanded((prev) => !prev)}
				>
					<Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
					<p className="flex-1 font-medium">
						These are transactions synced from your active data sources.
					</p>
					<ChevronDown
						className={cn(
							"mt-0.5 h-4 w-4 shrink-0 text-blue-500 transition-transform",
							bannerExpanded && "rotate-180",
						)}
					/>
				</button>
				{bannerExpanded && (
					<p className="mt-2 pl-7 text-blue-800 dark:text-blue-300">
						This table displays all transactions that have been automatically
						imported from your connected data sources. Recurring transactions
						created manually via the Recurring Transactions page are not
						included here — those are managed separately and feed directly into
						your forecast.
					</p>
				)}
			</div>

			{/* Account filter banner */}
			{activeAccountFilter && (
				<div className="flex items-center gap-3 px-4 py-3 bg-primary/10 border border-primary/20 rounded-lg">
					<Landmark className="h-4 w-4 text-primary shrink-0" />
					<div className="flex-1 text-sm">
						<span className="font-medium">Filtered by account: </span>
						<span className="text-muted-foreground">{activeAccountFilter}</span>
					</div>
					<Button
						variant="ghost"
						size="sm"
						className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
						onClick={() => setActiveAccountFilter(undefined)}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>
			)}

			{/* Error state (live mode only) */}
			{error && mode === "live" && (
				<div className="flex items-center justify-center py-12 text-destructive">
					{error}
				</div>
			)}

			{/* Maximus Table */}
			{!error && (
				<div className="min-h-[400px] flex-1 flex flex-col -mt-3">
					<MaximusTableReusable
						data={quickFilteredTransactions}
						columns={columns}
						title=""
						isLoading={isLoading && mode === "live"}
						searchPlaceholder="Search by description or category..."
						enableRowSelection={false}
						enableZebraStriping={true}
						onSortChange={mode === "live" ? handleSortChange : undefined}
						pagination={{
							enabled: true,
							defaultPageSize: 25,
							pageSizeOptions: [10, 25, 50, 100],
						}}
						onSearch={(data, query) => {
							const q = query.toLowerCase();
							return data.filter(
								(txn) =>
									txn.description.toLowerCase().includes(q) ||
									txn.category.toLowerCase().includes(q),
							);
						}}
						rowIdAccessor={(row) => String(row.id)}
						headerActions={
							<>
								<Button
									variant={filter === "all" ? "default" : "outline"}
									onClick={() => setFilter("all")}
									size="sm"
								>
									All ({getFilterCount("all")})
								</Button>
								<Button
									variant={filter === "thisWeek" ? "default" : "outline"}
									onClick={() => setFilter("thisWeek")}
									size="sm"
								>
									This Week ({getFilterCount("thisWeek")})
								</Button>
								<Button
									variant={filter === "uncategorized" ? "default" : "outline"}
									onClick={() => setFilter("uncategorized")}
									size="sm"
								>
									Uncategorised ({getFilterCount("uncategorized")})
								</Button>
								{auditMode && (
									<Popover>
										<PopoverTrigger asChild>
											<Button variant="ghost" size="sm" className="h-6 w-6 p-0">
												<Info className="h-4 w-4 text-blue-500" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-80" align="end">
											<div className="space-y-3">
												<div className="font-semibold text-sm border-b pb-2">
													🔍 Audit: Transaction Count Breakdown
												</div>
												<div className="space-y-2 text-sm">
													<div className="flex justify-between">
														<span className="text-muted-foreground">
															Total in system:
														</span>
														<span className="font-mono font-semibold">
															{allTransactions.length}
														</span>
													</div>
													<div className="flex justify-between">
														<span className="text-muted-foreground">
															After entity filter:
														</span>
														<span className="font-mono font-semibold">
															{entityFilteredTransactions.length}
														</span>
													</div>
													<div className="bg-blue-50 dark:bg-blue-950 p-2 rounded text-xs">
														<div className="font-semibold mb-1">
															Entity Filter Applied:
														</div>
														<div className="text-muted-foreground">
															Showing only transactions for:{" "}
															<span className="font-semibold text-foreground">
																Birmingham
															</span>
														</div>
													</div>
													<div className="flex justify-between pt-2 border-t">
														<span className="text-muted-foreground">
															After quick filter:
														</span>
														<span className="font-mono font-semibold text-blue-600">
															{quickFilteredTransactions.length}
														</span>
													</div>
												</div>
												<div className="text-xs text-muted-foreground pt-2 border-t">
													✓ All numbers verified from source data
												</div>
											</div>
										</PopoverContent>
									</Popover>
								)}
							</>
						}
					/>
				</div>
			)}

			<div className="flex justify-end">
				<FeatureFeedback feature="Transactions" />
			</div>

			<TransactionDetailModal
				open={showTransactionModal}
				onClose={() => {
					setShowTransactionModal(false);
					setSelectedTransaction(null);
				}}
				transaction={selectedTransaction as any}
				onSave={(updatedTransaction) => {
					if (
						selectedTransaction &&
						updatedTransaction.category !== selectedTransaction.category
					) {
						handleCategorySave(
							selectedTransaction,
							updatedTransaction.category as string,
						);
					}
				}}
			/>
		</div>
	);
};
